import React, { useState, useEffect, useCallback } from "react";
import { BookOpen } from "lucide-react";
import Toast, { showToast } from "./Toast";
import { STORAGE_KEYS, readStorage, saveStorage, addActivity, getSchoolSettings, loadModuleData, saveModuleData, addActivityAsync } from "./utils";

const EMPTY_DATA = {
  schoolName: "", schoolCode: "", board: "", schoolType: "", academicYear: "",
  principalName: "", classesOffered: "", medium: "", studentStrength: "", staffStrength: "",
  backgroundNote: "", avgClassSize: "", studentTeacherRatio: "", academicStrength: "",
  academicGap: "", academicOfferings: [], infrastructure: [],
  address: "", city: "", state: "", phone: "", email: "", website: "",
};

function Tag({ color, children }) {
  const colors = {
    green: "bg-[#eaf8f1] text-[#2d9b68] border border-[#b6e8d3]",
    red: "bg-[#fff0f0] text-[#d95353] border border-[#f5c0c0]",
    orange: "bg-[#fff4eb] text-[#f47c20] border border-[#f7c9a1]",
    blue: "bg-[#eef6fc] text-[#2d81c1] border border-[#cfe3f3]",
    gray: "bg-[#f5f7fb] text-[#617587] border border-[#e6ecf3]",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[color] || colors.gray}`}>
      {children}
    </span>
  );
}

function Window({ title, note, headerRight, children }) {
  return (
    <section className="bg-white rounded-[18px] border border-[#e6ecf3] shadow-sm overflow-hidden">
      <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-[#e6ecf3]">
        <div>
          <div className="font-semibold text-[#233445] text-[15px]">{title}</div>
          {note && <div className="text-xs text-[#617587] mt-0.5">{note}</div>}
        </div>
        {headerRight && <div className="flex items-center gap-2 shrink-0">{headerRight}</div>}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

function PreviewCard({ label, value }) {
  return (
    <div className="bg-[#f5f7fb] rounded-[14px] p-4 mb-3 last:mb-0">
      <div className="text-xs font-semibold text-[#617587] uppercase tracking-wide mb-1">{label}</div>
      <div className="text-sm text-[#233445]">{value || "Not added yet."}</div>
    </div>
  );
}

function InfoCard({ title, text }) {
  return (
    <div className="bg-[#eef6fc] rounded-[14px] p-4 mb-3 last:mb-0 border border-[#cfe3f3]">
      <div className="text-sm font-semibold text-[#2d81c1] mb-1">{title}</div>
      <div className="text-sm text-[#617587]">{text}</div>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
        <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-sm font-medium text-[#233445] mb-1.5">{label}</label>
      {children}
    </div>
  );
}

const INPUT = "w-full border border-[#e6ecf3] rounded-[10px] px-4 py-2.5 text-sm text-[#233445] placeholder-[#8ea0b1] focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white";
const TEXTAREA = "w-full border border-[#e6ecf3] rounded-[10px] px-4 py-3 text-sm text-[#233445] placeholder-[#8ea0b1] resize-none focus:outline-none focus:ring-2 focus:ring-[#f47c20]/20 focus:border-[#f47c20] bg-white";

function calculateCompletion(d) {
  const required = [
    d.schoolName, d.schoolCode, d.board, d.schoolType, d.academicYear,
    d.principalName, d.classesOffered, d.studentStrength, d.staffStrength,
    d.address, d.city, d.phone
  ];
  let filled = required.filter(Boolean).length;
  if (d.academicOfferings.length > 0) filled += 1;
  if (d.avgClassSize) filled += 1;
  if (d.studentTeacherRatio) filled += 1;
  return Math.round((filled / 15) * 100);
}

const SAMPLE_DATA = {
  schoolName: "ABC Public School", schoolCode: "APS-0147", board: "CBSE",
  schoolType: "Private Unaided", academicYear: "2026–27", principalName: "Dr. Meera Sharma",
  classesOffered: "Nursery to Grade 12", medium: "English", studentStrength: "1850", staffStrength: "112",
  backgroundNote: "ABC Public School is a mid-to-large urban school focused on academic excellence, discipline, and gradually improving future-readiness and experiential learning systems.",
  avgClassSize: "34", studentTeacherRatio: "17:1",
  academicStrength: "Strong board examination outcomes and disciplined classroom systems",
  academicGap: "Limited interdisciplinary and project-based learning exposure",
  academicOfferings: ["STEM Lab", "Olympiad Support", "Remedial Classes", "Robotics Club", "Art Integration"],
  infrastructure: [
    { name: "Science Lab", status: "Available", remark: "Fully functional for middle and senior grades." },
    { name: "Computer Lab", status: "Needs Upgrade", remark: "Hardware exists but systems need modernization." },
    { name: "Library", status: "Available", remark: "Well-stocked but underutilized in daily academic planning." },
  ],
  address: "Sector 9, Civil Lines Extension, Jaipur, Rajasthan",
  city: "Jaipur", state: "Rajasthan", phone: "+91 98765 43210",
  email: "info@abcpublicschool.edu", website: "https://www.abcpublicschool.edu",
};

export default function SchoolInformation() {
  const [activeTab, setActiveTab] = useState("profile");
  const [data, setData] = useState(EMPTY_DATA);
  const [offeringInput, setOfferingInput] = useState("");
  const school = getSchoolSettings();

  useEffect(() => {
    const stored = readStorage(STORAGE_KEYS.schoolInfo, null);
    if (stored) {
      const d = {
        ...EMPTY_DATA, ...stored,
        academicOfferings: Array.isArray(stored.academicOfferings) ? stored.academicOfferings : [],
        infrastructure: Array.isArray(stored.infrastructure) ? stored.infrastructure : [],
      };
      setData(d);
      setOfferingInput((d.academicOfferings || []).join(", "));
    }
    loadModuleData("schoolInfo", null).then(apiData => {
      if (apiData) {
        const d = {
          ...EMPTY_DATA, ...apiData,
          academicOfferings: Array.isArray(apiData.academicOfferings) ? apiData.academicOfferings : [],
          infrastructure: Array.isArray(apiData.infrastructure) ? apiData.infrastructure : [],
        };
        setData(d);
        setOfferingInput((d.academicOfferings || []).join(", "));
      }
    });
  }, []);

  const saveModule = useCallback((d, action = "School information saved") => {
    saveModuleData("schoolInfo", d, 0);
    addActivityAsync("School Information", action, "Saved", "green");
  }, []);

  const completion = calculateCompletion(data);
  const readiness = completion >= 85 ? "High" : completion >= 55 ? "Medium" : "Low";

  const profileStatus =
    completion >= 80 ? "This module now offers a strong baseline for diagnosis and planning."
    : completion >= 50 ? "A good amount of school baseline is saved, but some gaps still remain."
    : completion > 0 ? "Basic school data has started coming in, but the profile is still incomplete."
    : "No school profile has been saved yet.";

  const baselineNote =
    completion >= 85 ? "The school baseline is quite clear. Diagnosis and later strategy pages can now be built more realistically."
    : completion >= 55 ? "The baseline has a useful structure, but some key details should still be strengthened."
    : "The baseline is still weak because important school information is missing.";

  function handleSaveProfile(isDraft = false) {
    if (!data.schoolName || !data.schoolCode || !data.board) {
      showToast("School Name, School Code, and Board are required.", "error");
      return;
    }
    saveModule(data, isDraft ? "School profile draft saved" : "School profile saved");
    showToast(isDraft ? "School profile draft saved." : "School profile saved.", "success");
  }

  function handleSaveAcademic() {
    const offerings = offeringInput.split(",").map(s => s.trim()).filter(Boolean);
    const newData = { ...data, academicOfferings: offerings };
    if (!newData.avgClassSize && !newData.studentTeacherRatio && !offerings.length) {
      showToast("Add at least one academic detail before saving.", "error");
      return;
    }
    setData(newData);
    saveModule(newData, "Academic snapshot saved");
    showToast("Academic snapshot saved.", "success");
  }

  function handleSaveContact() {
    if (!data.address || !data.city || !data.phone) {
      showToast("Address, City, and Phone are required.", "error");
      return;
    }
    saveModule(data, "Contact details saved");
    showToast("Contact details saved.", "success");
  }

  function handleFillSample() {
    setData(SAMPLE_DATA);
    setOfferingInput(SAMPLE_DATA.academicOfferings.join(", "));
    saveModule(SAMPLE_DATA, "Sample school information loaded");
    showToast("Sample School Information content loaded.", "success");
  }

  function handleClear() {
    if (!window.confirm("This will clear all saved School Information. Continue?")) return;
    setData(EMPTY_DATA);
    setOfferingInput("");
    saveModuleData("schoolInfo", EMPTY_DATA, 0);
    addActivityAsync("School Information", "Module cleared", "Cleared", "red");
    showToast("School Information module cleared.", "warn");
  }

  const tabs = [
    { id: "profile", label: "School Profile" },
    { id: "academic", label: "Academic Snapshot" },
    { id: "contact", label: "Contact & Location" },
    { id: "preview", label: "Live Preview" },
  ];

  const extraControls = (
    <div className="flex items-center gap-2">
      <select
        className="text-sm border border-[#e6ecf3] rounded-[10px] px-3 py-1.5 bg-white text-[#233445] focus:outline-none"
        value={data.schoolType || ""}
        onChange={e => {
          const newData = { ...data, schoolType: e.target.value };
          setData(newData);
          saveModule(newData, "School type updated");
          showToast("School type updated.", "success");
        }}
      >
        <option value="">School Type</option>
        <option>Private Unaided</option>
        <option>Government</option>
        <option>Aided</option>
        <option>International</option>
      </select>
      <button onClick={handleFillSample} className="text-sm px-3 py-1.5 rounded-[10px] bg-[#fff4eb] text-[#f47c20] border border-[#f7c9a1] hover:bg-[#ffe8d0] transition-colors" title="Fill sample data">✨</button>
      <button onClick={handleClear} className="text-sm px-3 py-1.5 rounded-[10px] bg-[#fff0f0] text-[#d95353] border border-[#f5c0c0] hover:bg-[#ffe0e0] transition-colors" title="Clear module">⌫</button>
    </div>
  );

  function upd(field) {
    return e => setData(d => ({ ...d, [field]: e.target.value }));
  }

  return (
    <>
      <div className="flex-1 flex flex-col min-w-0 border-l-2 border-slate-200 ml-1">

        <div className="flex justify-center pl-10 pr-6 py-6">
          <main className="w-full">
          {/* Hero Header */}
          <section className="mb-8">
            <div className="text-center px-4 py-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6 bg-[#D67C2C]">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-5xl font-bold text-[#D67C2C] mb-4">School Information</h1>
              <p className="text-[#1D3469] text-[18px] sm:text-[20px] font-medium max-w-5xl mx-auto px-4 leading-[1.4]">
                Define your school profile, academic snapshot, and contact baseline for connected planning.
              </p>
            </div>
          </section>

          {/* Stats */}
          <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              { label: "Completion", icon: "📌", iconCls: "bg-[#fff4eb] text-[#f47c20] border-[#f7c9a1]", value: `${completion}%`, meta: "of profile filled" },
              { label: "Academic Offerings", icon: "📘", iconCls: "bg-[#eaf8f1] text-[#2d9b68] border-[#cfe8dc]", value: data.academicOfferings.length, meta: "programs listed" },
              { label: "Student Strength", icon: "👥", iconCls: "bg-[#eef6fc] text-[#2d81c1] border-[#cfe3f3]", value: data.studentStrength || "—", meta: "students recorded" },
              { label: "Readiness", icon: "⚑", iconCls: "bg-[#fff7e7] text-[#d58d1f] border-[#f4deab]", value: readiness, meta: "baseline clarity" },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 min-h-[130px] flex flex-col justify-between">
                <div className="flex items-center justify-between gap-[10px] mb-4">
                  <span className="text-[12px] text-[#617587] font-medium">{s.label}</span>
                  <div className={`w-[42px] h-[42px] rounded-[14px] grid place-items-center text-[18px] border ${s.iconCls}`}>
                    {s.icon}
                  </div>
                </div>
                <div>
                  <div className="text-[28px] font-bold leading-none text-[#233445] mb-[6px]">{s.value}</div>
                  <div className="text-[12px] text-[#617587]">{s.meta}</div>
                </div>
              </div>
            ))}
          </section>

          {/* Progress */}
          <div className="bg-white rounded-[18px] border border-[#e6ecf3] shadow-sm p-5 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-[#233445]">Module Completion</span>
              <span className="text-sm font-bold text-[#f47c20]">{completion}%</span>
            </div>
            <div className="h-2 bg-[#f5f7fb] rounded-full overflow-hidden">
              <div className="h-full bg-[#f47c20] rounded-full transition-all duration-500" style={{ width: `${completion}%` }} />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-1 mb-6 bg-white rounded-[18px] border border-[#e6ecf3] p-1.5 shadow-sm w-fit">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2 rounded-[12px] text-sm font-medium transition-colors ${activeTab === t.id ? "bg-[#f47c20] text-white shadow-sm" : "text-[#617587] hover:text-[#233445] hover:bg-[#f5f7fb]"}`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* PROFILE TAB */}
          {activeTab === "profile" && (
            <div className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-5">
              <Window
                title="Basic School Profile"
                note="Capture the core details that define the school institutionally."
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="School Name"><input className={INPUT} placeholder="Enter school name..." value={data.schoolName} onChange={upd("schoolName")} /></Field>
                  <Field label="School Code / ID"><input className={INPUT} placeholder="Enter school code..." value={data.schoolCode} onChange={upd("schoolCode")} /></Field>
                  <Field label="Board / Affiliation">
                    <select className={INPUT} value={data.board} onChange={upd("board")}>
                      <option value="">Select board</option>
                      <option>CBSE</option><option>ICSE</option><option>State Board</option><option>International Board</option><option>Other</option>
                    </select>
                  </Field>
                  <Field label="School Type">
                    <select className={INPUT} value={data.schoolType} onChange={upd("schoolType")}>
                      <option value="">Select type</option>
                      <option>Private Unaided</option><option>Government</option><option>Aided</option><option>International</option>
                    </select>
                  </Field>
                  <Field label="Academic Year"><input className={INPUT} placeholder="Example: 2026–27" value={data.academicYear} onChange={upd("academicYear")} /></Field>
                  <Field label="Principal Name"><input className={INPUT} placeholder="Enter principal name..." value={data.principalName} onChange={upd("principalName")} /></Field>
                  <Field label="Classes Offered"><input className={INPUT} placeholder="Example: Nursery to Grade 12" value={data.classesOffered} onChange={upd("classesOffered")} /></Field>
                  <Field label="Medium of Instruction"><input className={INPUT} placeholder="Example: English" value={data.medium} onChange={upd("medium")} /></Field>
                  <Field label="Student Strength"><input type="number" className={INPUT} placeholder="Enter student count..." value={data.studentStrength} onChange={upd("studentStrength")} /></Field>
                  <Field label="Staff Strength"><input type="number" className={INPUT} placeholder="Enter staff count..." value={data.staffStrength} onChange={upd("staffStrength")} /></Field>
                  <Field label="School Background Note" full>
                    <textarea className={TEXTAREA} rows={3} placeholder="Briefly describe the school's context, history, and current positioning..." value={data.backgroundNote} onChange={upd("backgroundNote")} />
                  </Field>
                </div>
                <div className="mt-4 flex items-center justify-between gap-4 p-3 bg-[#f5f7fb] rounded-[10px]">
                  <div className="text-sm text-[#617587]">{profileStatus}</div>
                  <div className="shrink-0 flex items-center gap-2">
                    <button onClick={() => setData(d => ({ ...d, schoolName: "", schoolCode: "", board: "", schoolType: "", academicYear: "", principalName: "", classesOffered: "", medium: "", studentStrength: "", staffStrength: "", backgroundNote: "" }))} className="text-xs px-3 py-1.5 rounded-[8px] bg-white text-[#617587] border border-[#e6ecf3] hover:bg-[#e6ecf3] transition-colors">Reset Fields</button>
                    <button onClick={() => handleSaveProfile(true)} className="text-xs px-3 py-1.5 rounded-[8px] bg-white text-[#617587] border border-[#e6ecf3] hover:bg-[#e6ecf3] transition-colors">Save Draft</button>
                    <button onClick={() => handleSaveProfile(false)} className="text-xs px-3 py-1.5 rounded-[8px] bg-[#f47c20] text-white hover:bg-[#d96d18] transition-colors">Save Profile</button>
                  </div>
                </div>
              </Window>

              <Window title="Why this matters" note="How this page supports later planning.">
                <InfoCard title="Creates the factual baseline" text="This page gives the basic reference for what kind of school is being planned for." />
                <InfoCard title="Supports diagnosis" text="Without school size, board, class range, and staff strength, later diagnosis becomes too vague." />
                <InfoCard title="Makes goals more realistic" text="A school serving 350 students and a school serving 2500 students will need very different growth expectations." />
                <InfoCard title="Feeds the final compiled view" text="Everything saved here will appear later inside Reports and Complete School View." />
              </Window>
            </div>
          )}

          {/* ACADEMIC TAB */}
          {activeTab === "academic" && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <Window
                title="Academic Snapshot"
                note="Core teaching and learning structure at a glance."
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Average Class Size"><input type="number" className={INPUT} placeholder="Example: 32" value={data.avgClassSize} onChange={upd("avgClassSize")} /></Field>
                  <Field label="Student-Teacher Ratio"><input className={INPUT} placeholder="Example: 18:1" value={data.studentTeacherRatio} onChange={upd("studentTeacherRatio")} /></Field>
                  <Field label="Key Academic Strength"><input className={INPUT} placeholder="Example: Strong board exam performance" value={data.academicStrength} onChange={upd("academicStrength")} /></Field>
                  <Field label="Key Academic Gap"><input className={INPUT} placeholder="Example: Weak project-based learning culture" value={data.academicGap} onChange={upd("academicGap")} /></Field>
                  <Field label="Academic Offerings / Special Programs" full>
                    <textarea className={TEXTAREA} rows={3} placeholder="Add one offering at a time, separated by commas if needed... Example: STEM lab, remedial classes, Olympiad support, robotics, art integration" value={offeringInput} onChange={e => setOfferingInput(e.target.value)} />
                  </Field>
                </div>
                <div className="mt-4 flex items-center justify-end gap-2">
                  <button onClick={() => setData(d => ({ ...d, avgClassSize: "", studentTeacherRatio: "", academicStrength: "", academicGap: "" })) || setOfferingInput("")} className="px-4 py-2 rounded-[10px] text-sm font-medium bg-[#f5f7fb] text-[#617587] border border-[#e6ecf3] hover:bg-[#e6ecf3] transition-colors">Reset</button>
                  <button onClick={handleSaveAcademic} className="px-4 py-2 rounded-[10px] text-sm font-medium bg-[#f47c20] text-white hover:bg-[#d96d18] transition-colors">Save Academic Snapshot</button>
                </div>
              </Window>

              <Window title="Saved Academic Snapshot" note="Quick view of the currently saved academic data.">
                <PreviewCard label="Average Class Size" value={data.avgClassSize} />
                <PreviewCard label="Student-Teacher Ratio" value={data.studentTeacherRatio} />
                <PreviewCard label="Key Strength / Gap" value={[data.academicStrength, data.academicGap].filter(Boolean).join(" | ") || null} />
                <div className="overflow-x-auto rounded-[14px] border border-[#e6ecf3] mt-4">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#f5f7fb] border-b border-[#e6ecf3]">
                        <th className="text-left px-4 py-3 font-semibold text-[#617587] text-xs uppercase tracking-wide">Academic Offering</th>
                        <th className="text-left px-4 py-3 font-semibold text-[#617587] text-xs uppercase tracking-wide">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.academicOfferings.length === 0 ? (
                        <tr><td colSpan={2} className="text-center py-6 text-[#8ea0b1] text-sm">No academic offerings saved yet.</td></tr>
                      ) : data.academicOfferings.map((item, idx) => (
                        <tr key={idx} className="border-b border-[#e6ecf3] last:border-0">
                          <td className="px-4 py-3 text-[#233445]">{item}</td>
                          <td className="px-4 py-3"><Tag color={idx % 2 === 0 ? "blue" : "green"}>Saved</Tag></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Window>
            </div>
          )}

          {/* CONTACT TAB */}
          {activeTab === "contact" && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <Window
                title="Contact & Location"
                note="Save the operating location and communication details."
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Full Address" full><textarea className={TEXTAREA} rows={2} placeholder="Enter complete school address..." value={data.address} onChange={upd("address")} /></Field>
                  <Field label="City"><input className={INPUT} placeholder="Enter city..." value={data.city} onChange={upd("city")} /></Field>
                  <Field label="State"><input className={INPUT} placeholder="Enter state..." value={data.state} onChange={upd("state")} /></Field>
                  <Field label="Phone Number"><input className={INPUT} placeholder="Enter contact number..." value={data.phone} onChange={upd("phone")} /></Field>
                  <Field label="Email"><input type="email" className={INPUT} placeholder="Enter email..." value={data.email} onChange={upd("email")} /></Field>
                  <Field label="Website / Portal Link" full><input className={INPUT} placeholder="Enter website or portal link..." value={data.website} onChange={upd("website")} /></Field>
                </div>
                <div className="mt-4 flex items-center justify-end gap-2">
                  <button onClick={() => setData(d => ({ ...d, address: "", city: "", state: "", phone: "", email: "", website: "" }))} className="px-4 py-2 rounded-[10px] text-sm font-medium bg-[#f5f7fb] text-[#617587] border border-[#e6ecf3] hover:bg-[#e6ecf3] transition-colors">Reset</button>
                  <button onClick={handleSaveContact} className="px-4 py-2 rounded-[10px] text-sm font-medium bg-[#f47c20] text-white hover:bg-[#d96d18] transition-colors">Save Contact Details</button>
                </div>
              </Window>

              <Window title="Saved Contact Preview" note="Simple compiled contact view.">
                <PreviewCard label="Address" value={data.address} />
                <PreviewCard label="City / State" value={[data.city, data.state].filter(Boolean).join(", ") || null} />
                <PreviewCard label="Phone / Email" value={[data.phone, data.email].filter(Boolean).join(" | ") || null} />
                <PreviewCard label="Website" value={data.website} />
              </Window>
            </div>
          )}

          {/* PREVIEW TAB */}
          {activeTab === "preview" && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
              <Window title="Live School Summary" note="Current saved school information at a glance.">
                <PreviewCard
                  label="School"
                  value={[data.schoolName, data.board, data.schoolType].filter(Boolean).join(" | ") || null}
                />
                <PreviewCard
                  label="Institutional Snapshot"
                  value={[
                    data.principalName ? `Principal: ${data.principalName}` : "",
                    data.classesOffered ? `Classes: ${data.classesOffered}` : "",
                    data.medium ? `Medium: ${data.medium}` : "",
                  ].filter(Boolean).join(" | ") || null}
                />
                <PreviewCard
                  label="Operational Snapshot"
                  value={[
                    data.studentStrength ? `Students: ${data.studentStrength}` : "",
                    data.staffStrength ? `Staff: ${data.staffStrength}` : "",
                  ].filter(Boolean).join(" | ") || null}
                />
                <PreviewCard
                  label="Academic Snapshot"
                  value={[
                    data.avgClassSize ? `Avg. Class Size: ${data.avgClassSize}` : "",
                    data.studentTeacherRatio ? `Ratio: ${data.studentTeacherRatio}` : "",
                    data.academicOfferings.length ? `Offerings: ${data.academicOfferings.length}` : "",
                  ].filter(Boolean).join(" | ") || null}
                />
              </Window>

              <Window title="Readiness Interpretation" note="What this page currently tells us.">
                <InfoCard title="School baseline clarity" text={baselineNote} />
                <InfoCard title="Why this matters next" text="This page should make diagnosis and SWOT more grounded by clarifying who the school is, what it serves, and how it is set up operationally." />
                <InfoCard title="Next connected page" text="After this, move to Teams & Committees so accountability structure becomes visible before strategic planning deepens." />
              </Window>
            </div>
          )}

          <section className="mt-8 bg-white rounded-[24px] border border-[#DDE6F4] shadow-sm overflow-hidden">
            <div className="px-7 py-5 border-b border-[#e6ecf3] flex items-center justify-between gap-4">
              <div>
                <h3 className="text-lg font-semibold text-[#1D3469]">Module Actions</h3>
                <p className="text-sm text-[#5E6B84] mt-1">Keep actions centralized for a clean workspace layout.</p>
              </div>
            </div>
            <div className="px-7 py-5 flex flex-wrap items-center gap-3">
              <button onClick={handleFillSample} className="px-5 py-2.5 rounded-[12px] bg-[#fff4eb] text-[#f47c20] border border-[#f7c9a1] hover:bg-[#ffe8d0] transition-colors text-sm font-semibold">Load Sample Data</button>
              <button onClick={handleClear} className="px-5 py-2.5 rounded-[12px] bg-[#fff0f0] text-[#d95353] border border-[#f5c0c0] hover:bg-[#ffe0e0] transition-colors text-sm font-semibold">Clear Module</button>
            </div>
          </section>
          </main>
        </div>
      </div>
      <Toast />
    </>
  );
}
